import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-[2010] overflow-hidden rounded-md bg-popover px-3 py-1.5 text-xs text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

interface ButtonTooltipProps {
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  side?: React.ComponentProps<typeof TooltipContent>["side"];
  hidden?: boolean;
  children: React.ReactNode;
}

/**
 * Shared tooltip wrapper for icon-only action buttons (right editor sidebar
 * rail, left project sidebar's fixed icon row, ...) so every surface shows
 * the same always-on-hover tooltip instead of each button re-implementing
 * its own Tooltip/TooltipContent wiring.
 */
const ButtonTooltip: React.FC<ButtonTooltipProps> = ({ tooltip, side = "right", hidden, children }) => {
  if (!tooltip) {
    return <>{children}</>
  }

  const tooltipProps = typeof tooltip === "string" ? { children: tooltip } : tooltip
  const triggerChild = React.Children.toArray(children).find(React.isValidElement)

  if (!triggerChild) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{triggerChild}</TooltipTrigger>
      <TooltipContent
        side={side}
        align="center"
        hidden={hidden}
        className="bg-popover text-popover-foreground border border-border shadow-md"
        {...tooltipProps}
      />
    </Tooltip>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, ButtonTooltip }
